/**
 * Phase 8A — MIS list screen. Lists all MISes visible to the user.
 * Create/Edit/Delete gated to EAF.
 */

import { mdiPencil, mdiPlus, mdiRefresh, mdiTrashCan } from "@mdi/js";
import Icon from "@mdi/react";
import moment from "moment";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../context_providers/auth/authProvider";
import LoadingWidget from "../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../context_providers/toast/toast_ctx";
import {
  fetchDelete,
  fetchGet,
  fetchPost,
  fetchPut,
} from "../../service/api_client";
import { ACTION, MODULE } from "../../utils/enums";
import { urls } from "../../utils/urls";
import MisCreateEditModal from "./components/mis_create_edit_modal";
import {
  ACTUALS_SOURCE_LABEL,
  CADENCE_LABEL,
  Mis,
  MisActualsSource,
  MisCadenceMonths,
} from "./models/mis_model";

const MisListScreen: FunctionComponent = () => {
  const baseUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/mis`;
  const nav = useHistory();
  const { showToast } = useToast();
  const { isAuthorised } = useAuth();

  const [data, setData] = useState<Mis[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Mis | null>(null);

  const canManage =
    isAuthorised({ module: MODULE.mis, action: ACTION.CREATE }) ||
    isAuthorised({ module: MODULE.mis, action: ACTION.UPDATE });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchGet(baseUrl);
      if (res.success) {
        setData(res.data || []);
      } else {
        showToast({ type: ToastType.error, text: res.error || "Failed to load MIS." });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e?.message || "Network error." });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const handleEdit = (m: Mis) => {
    setEditing(m);
    setModalOpen(true);
  };

  const handleSubmit = async (input: {
    name: string;
    description: string;
    cadence_months: MisCadenceMonths;
    start_fy: string;
    actuals_source: MisActualsSource;
  }) => {
    const res = editing
      ? await fetchPut(`${baseUrl}/${editing.id}`, input)
      : await fetchPost(baseUrl, input);
    if (!res.success) throw new Error(res.error || "Save failed");
    showToast({
      type: ToastType.success,
      text: editing ? "MIS updated" : "MIS created",
    });
    await fetchData();
  };

  const handleDelete = async (m: Mis) => {
    if (
      !window.confirm(
        `Deactivate MIS "${m.name}"? Underlying data is preserved and the MIS can be re-activated later.`,
      )
    )
      return;
    const res = await fetchDelete(`${baseUrl}/${m.id}`);
    if (res.success) {
      showToast({ type: ToastType.success, text: "MIS deactivated" });
      fetchData();
    } else {
      showToast({ type: ToastType.error, text: res.error || "Delete failed" });
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">MIS</h1>
          <p className="text-sm text-gray-500">
            P&amp;L generator — build and view dynamic MIS reports.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            className="border border-gray-300 rounded px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-1"
          >
            <Icon path={mdiRefresh} className="h-4 w-4" />
            Refresh
          </button>
          {canManage && (
            <button
              onClick={handleCreate}
              className="bg-myPrimaryColor hover:bg-mySecondaryColor text-white rounded px-4 py-2 text-sm font-semibold flex items-center gap-1"
            >
              <Icon path={mdiPlus} className="h-4 w-4" />
              Create MIS
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <LoadingWidget />
      ) : data.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
          No MIS reports yet.{" "}
          {canManage && (
            <>
              Click{" "}
              <button
                onClick={handleCreate}
                className="text-myPrimaryColor hover:underline underline"
              >
                Create MIS
              </button>{" "}
              to get started.
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((m) => (
            <div
              key={m.id}
              onClick={() => nav.push(`${urls.mis_view}/${m.id}`)}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-myPrimaryColor transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-gray-800 text-base">
                    {m.name}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {m.start_fy} · {CADENCE_LABEL[m.cadence_months]}
                  </p>
                </div>
                {canManage && (
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleEdit(m)}
                      className="text-gray-500 hover:text-myPrimaryColor p-1"
                      title="Edit"
                    >
                      <Icon path={mdiPencil} className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(m)}
                      className="text-gray-500 hover:text-red-600 p-1"
                      title="Deactivate"
                    >
                      <Icon path={mdiTrashCan} className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              {m.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {m.description}
                </p>
              )}
              <div className="text-xs text-gray-500 space-y-0.5">
                <div>
                  <span className="font-medium text-gray-600">Actuals:</span>{" "}
                  {ACTUALS_SOURCE_LABEL[m.actuals_source]}
                </div>
                <div>
                  <span className="font-medium text-gray-600">Created:</span>{" "}
                  {moment(m.created_at).format("DD MMM YYYY")}
                  {m.created_by_name ? ` · ${m.created_by_name}` : ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <MisCreateEditModal
        open={modalOpen}
        initial={editing}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default MisListScreen;
